import type { ComponentProps } from 'react';
import React, { useCallback } from 'react';
import { useLocalSearchParams, useRouter } from 'expo-router';

import { useFeatureFlag } from '@order/LaunchDarkly';
import { useTelemetry } from '@order/Telemetry';

import { LocationSearch, NotFoundView, TransparentModal } from '../../components';
import { useLastInteractedStoreMachine } from '../../context';
import { useNavigateBack } from '../../navigation';
import { QuickAddModal } from './components';

// ─────────────────────────────────────────────────────────────────────────────

export const QuickAddLocationScreen = () => {
  const isQuickAddEnabled = useFeatureFlag('CELS-5657-order-quick-add-modifiable-product-enabled');

  if (!isQuickAddEnabled) {
    return (
      <QuickAddModal>
        <NotFoundView withoutFooter />
      </QuickAddModal>
    );
  }

  return <QuickAddLocationScreenReady />;
};

// ─────────────────────────────────────────────────────────────────────────────

const QuickAddLocationScreenReady = () => {
  const router = useRouter();
  const navigateBack = useNavigateBack();
  const { send: sendToLastInteractedStoreMachine } = useLastInteractedStoreMachine();
  const { track } = useTelemetry();

  const { baseProductLineageKey, sharedLineItemId } = useLocalSearchParams<RouteParams>();

  // ─── Handlers ────────────────────────────────────────────────────────

  const handleDismiss = useCallback(() => {
    navigateBack();
  }, [navigateBack]);

  const navigateToLocation = useCallback<OnOpenRestaurant>(
    (context) => {
      if (!context.activeLocation) return;

      const { restaurantId, restaurantName, restaurantSlug } = context.activeLocation;

      sendToLastInteractedStoreMachine({ type: 'RESET_ACTIVE_CART_STORE' });
      sendToLastInteractedStoreMachine({
        type: 'SET_LAST_INTERACTED_MENU_STORE',
        store: { restaurantId, restaurantName, restaurantSlug },
      });

      track('quick-add.location-change-success', {
        baseProductLineageKey,
        sharedLineItemId,
        restaurantId,
        restaurantSlug,
      });
      router.back();
    },
    [baseProductLineageKey, router, sendToLastInteractedStoreMachine, sharedLineItemId, track],
  );

  const navigateToDeliveryLocation = useCallback<OnOpenDeliveryRestaurant>(
    (context) => {
      const { deliveryLocation } = context;

      if (!deliveryLocation) return;

      const {
        addressId,
        restaurantId,
        restaurantName,
        restaurantSlug,
        restaurantDeliveryFee,
        deliveryVendorName,
        deliveryVendorRestaurantId,
      } = deliveryLocation;

      sendToLastInteractedStoreMachine({ type: 'RESET_ACTIVE_CART_STORE' });
      sendToLastInteractedStoreMachine({
        type: 'SET_LAST_INTERACTED_MENU_STORE',
        store: {
          restaurantId,
          restaurantName,
          restaurantSlug,
          addressId,
          deliveryFee: restaurantDeliveryFee,
          vendor: deliveryVendorName,
          vendorRestaurantId: deliveryVendorRestaurantId,
        },
      });

      track('quick-add.location-change-success', {
        baseProductLineageKey,
        sharedLineItemId,
        restaurantId,
        restaurantSlug,
        addressId,
      });
      router.back();
    },
    [baseProductLineageKey, router, sendToLastInteractedStoreMachine, sharedLineItemId, track],
  );

  // ─────────────────────────────────────────────────────────────────────

  return (
    <TransparentModal size="large" offset="10%" onClose={handleDismiss}>
      <LocationSearch
        navigateToLocation={navigateToLocation}
        navigateToDeliveryLocation={navigateToDeliveryLocation}
        interactedLocationRestaurantSlug={undefined}
        interactedLocationDeliveryAddressId={undefined}
        isInsideModal
      />
    </TransparentModal>
  );
};

// ─── Types ───────────────────────────────────────────────────────────────────

type RouteParams = {
  baseProductLineageKey: string;
  sharedLineItemId?: string;
};

type LocationSearchProps = ComponentProps<typeof LocationSearch>;

type OnOpenRestaurant = NonNullable<LocationSearchProps['navigateToLocation']>;
type OnOpenDeliveryRestaurant = NonNullable<LocationSearchProps['navigateToDeliveryLocation']>;
